import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  MapPin, 
  Compass, 
  Sparkles, 
  User, 
  Home,
  Bookmark,
  Users,
  ArrowRight,
  LogOut,
  Globe,
  Star,
  Plane,
  Map,
  MessageCircle,
  Zap,
  Heart,
  Calendar,
  Train,
  TrendingUp
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useLocation as useLocationContext } from '../contexts/LocationContext';
import api from '../services/api';
import LandingCarousel, { useCarouselVisibility } from '../components/LandingCarousel';
import { LocationDropdown } from '../components/LocationDropdown';

// Panchi Bird Logo
const PanchiBirdLogo = ({ className = "w-10 h-10" }) => (
  <img 
    src="https://customer-assets.emergentagent.com/job_panchi-travel-1/artifacts/5hhgrsb0_20260119_170240_0000.png"
    alt="Panchi"
    className={className}
    style={{ objectFit: 'contain' }}
  />
);

// Animated Travel Elements
const FloatingPlane = ({ delay = 0, className = "" }) => (
  <motion.div
    className={`absolute text-3xl ${className}`}
    animate={{ 
      x: [0, 20, 0],
      y: [0, -15, 0],
      rotate: [0, 5, 0]
    }}
    transition={{ 
      duration: 4, 
      repeat: Infinity, 
      delay,
      ease: "easeInOut"
    }}
  >
    ✈️
  </motion.div>
);

const FloatingBalloon = ({ delay = 0, className = "" }) => (
  <motion.div
    className={`absolute text-4xl ${className}`}
    animate={{ 
      y: [0, -20, 0],
      rotate: [-5, 5, -5]
    }}
    transition={{ 
      duration: 5, 
      repeat: Infinity, 
      delay,
      ease: "easeInOut"
    }}
  >
    🎈
  </motion.div>
);

const FloatingCloud = ({ delay = 0, className = "", size = "text-5xl" }) => (
  <motion.div
    className={`absolute ${size} opacity-60 ${className}`}
    animate={{ 
      x: [0, 30, 0],
    }}
    transition={{ 
      duration: 8, 
      repeat: Infinity, 
      delay,
      ease: "easeInOut"
    }}
  >
    ☁️
  </motion.div>
);

const FloatingSuitcase = ({ delay = 0, className = "" }) => (
  <motion.div
    className={`absolute text-2xl ${className}`}
    animate={{ 
      y: [0, -10, 0],
      rotate: [-3, 3, -3]
    }}
    transition={{ 
      duration: 3, 
      repeat: Infinity, 
      delay,
      ease: "easeInOut"
    }}
  >
    🧳
  </motion.div>
);

const FloatingGlobe = ({ delay = 0, className = "" }) => (
  <motion.div
    className={`absolute text-3xl ${className}`}
    animate={{ 
      rotate: [0, 360]
    }}
    transition={{ 
      duration: 20, 
      repeat: Infinity, 
      delay,
      ease: "linear"
    }}
  >
    🌍
  </motion.div>
);

// Floating Help Button - Perfectly Round
const FloatingHelpButton = () => (
  <motion.button
    initial={{ scale: 0 }}
    animate={{ scale: 1 }}
    whileHover={{ scale: 1.1 }}
    whileTap={{ scale: 0.95 }}
    className="fixed bottom-24 md:bottom-8 right-4 md:right-8 z-50 w-14 h-14 bg-gradient-to-br from-teal-500 to-cyan-500 text-white shadow-lg shadow-teal-500/30 hover:shadow-xl hover:shadow-teal-500/40 transition-shadow flex items-center justify-center"
    style={{ borderRadius: '50%' }}
    onClick={() => window.open('/support', '_self')}
    data-testid="floating-help-btn"
  >
    <MessageCircle size={24} />
  </motion.button>
);

// Hero images
const HERO_IMAGES = [
  'https://images.unsplash.com/photo-1506929562872-bb421503ef21?w=1920&q=80',
  'https://images.unsplash.com/photo-1507525428034-b723cf961d3e?w=1920&q=80',
  'https://images.unsplash.com/photo-1476514525535-07fb3b4ae5f1?w=1920&q=80',
  'https://images.unsplash.com/photo-1530789253388-582c481c54b0?w=1920&q=80',
];

// Global destinations with transport options
const DESTINATIONS = [
  { 
    name: 'Goa', 
    image: 'https://images.unsplash.com/photo-1512343879784-a960bf40e7f2?w=600&q=80', 
    country: 'India', 
    flag: '🇮🇳',
    rating: 4.9,
    trending: true,
    transport: { train: true, flight: true, trainTime: '10-12h from Mumbai' }
  },
  { 
    name: 'Jaipur', 
    image: 'https://images.unsplash.com/photo-1477587458883-47145ed94245?w=600&q=80', 
    country: 'India', 
    flag: '🇮🇳',
    rating: 4.8,
    transport: { train: true, flight: true, trainTime: '4-5h from Delhi' }
  },
  { 
    name: 'Kerala', 
    image: 'https://images.unsplash.com/photo-1602216056096-3b40cc0c9944?w=600&q=80', 
    country: 'India', 
    flag: '🇮🇳',
    rating: 4.9,
    trending: true,
    transport: { train: true, flight: true, trainTime: '12-16h from Bangalore' }
  },
  { 
    name: 'Varanasi', 
    image: 'https://images.unsplash.com/photo-1561361513-2d000a50f0dc?w=600&q=80', 
    country: 'India', 
    flag: '🇮🇳',
    rating: 4.7,
    transport: { train: true, flight: true, trainTime: '8-10h from Delhi' }
  },
  { 
    name: 'Dubai', 
    image: 'https://images.unsplash.com/photo-1512453979798-5ea266f8880c?w=600&q=80', 
    country: 'UAE', 
    flag: '🇦🇪',
    rating: 4.8,
    transport: { train: false, flight: true }
  },
  { 
    name: 'Manali', 
    image: 'https://images.unsplash.com/photo-1626621341517-bbf3d9990a23?w=600&q=80', 
    country: 'India', 
    flag: '🇮🇳',
    rating: 4.8,
    trending: true,
    transport: { train: true, flight: false, trainTime: '12-14h from Delhi to Chandigarh' }
  },
];

// Note: REGIONAL_EVENTS removed - now using AI-powered dynamic events
// Events are fetched from /api/events/ai/trending and /api/events/ipl/upcoming

export default function Landing() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, isAuthenticated, logout } = useAuth();
  const { country, city: locationCity, currency, currencySymbol } = useLocationContext();
  
  const [currentBg, setCurrentBg] = useState(0);
  const [userCity, setUserCity] = useState('');
  const [nearbyEvents, setNearbyEvents] = useState([]);
  const [loadingEvents, setLoadingEvents] = useState(true);
  
  // Carousel visibility
  const { showCarousel, hideCarousel } = useCarouselVisibility();
  
  // Auto-change background
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentBg(prev => (prev + 1) % HERO_IMAGES.length);
    }, 5000);
    return () => clearInterval(timer);
  }, []);
  
  // Load upcoming events in chronological order - using AI-powered events
  useEffect(() => {
    const loadEvents = async () => {
      setLoadingEvents(true);
      try {
        // Try travel-worthy events first (festivals, sports, holidays)
        const travelRes = await api.getAITravelWorthyEvents(8).catch(() => null);
        
        if (travelRes?.success && travelRes?.events?.length > 0) {
          let eventsToShow = travelRes.events;
          const isIndia = (country || '').toLowerCase() === 'india' || (country || '').toLowerCase() === 'in';
          
          if (isIndia) {
            const indianEvents = eventsToShow.filter(e => (e.country || '').toLowerCase() === 'india');
            const globalEvents = eventsToShow.filter(e => (e.country || '').toLowerCase() !== 'india');
            eventsToShow = [...indianEvents.slice(0, 3), ...globalEvents.slice(0, 1)];
            if (eventsToShow.length < 4) eventsToShow = travelRes.events.slice(0, 4);
          }
          
          const formattedEvents = eventsToShow.slice(0, 4).map(e => ({
            name: e.name,
            city: e.city,
            country: e.country || 'International',
            date: e.date ? new Date(e.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : 'Upcoming',
            image: e.image || 'https://images.unsplash.com/photo-1488646953014-85cb44e25828?w=400&q=80',
            category: e.category?.charAt(0).toUpperCase() + e.category?.slice(1) || 'Event',
            is_featured: e.is_featured,
            days_until: e.days_until
          }));
          setNearbyEvents(formattedEvents);
        } else {
          // Fallback to AI trending events
          const aiResponse = await api.getAITrendingEvents(8).catch(() => null);
          
          if (aiResponse?.success && aiResponse?.events?.length > 0) {
            const formattedEvents = aiResponse.events.slice(0, 4).map(e => ({
              name: e.name,
              city: e.city,
              country: e.country || 'International',
              date: e.date ? new Date(e.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : 'Upcoming',
              image: e.image || 'https://images.unsplash.com/photo-1488646953014-85cb44e25828?w=400&q=80',
              category: e.category?.charAt(0).toUpperCase() + e.category?.slice(1) || 'Event',
              is_featured: e.is_featured,
              days_until: e.days_until
            }));
            setNearbyEvents(formattedEvents);
          } else {
            // Final fallback to IPL events
            const iplResponse = await api.getUpcomingIPLEvents(4).catch(() => null);
            if (iplResponse?.success && iplResponse?.events?.length > 0) {
              const iplEvents = iplResponse.events.slice(0, 4).map(e => ({
                name: e.short_name || e.name,
                city: e.city,
                country: 'India',
                date: e.formatted_date || new Date(e.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
                image: e.image || 'https://images.unsplash.com/photo-1531415074968-036ba1b575da?w=400&q=80',
                category: 'Sports',
                is_featured: true
              }));
              setNearbyEvents(iplEvents);
            } else {
              setNearbyEvents([]);
            }
          }
        }
      } catch (error) {
        console.error('Error loading events:', error);
        setNearbyEvents([]); // Show empty state instead of hardcoded past events
      } finally {
        setLoadingEvents(false);
      }
    };
    
    loadEvents();
  }, [country]);
  
  // Detect user location (legacy - for origin city)
  useEffect(() => {
    // Invalid server-side locations that shouldn't be used
    const invalidLocations = ['council bluffs', 'ashburn', 'boardman', 'the dalles'];
    
    // Use LocationContext city if available and valid
    if (locationCity) {
      if (!invalidLocations.includes(locationCity.toLowerCase())) {
        setUserCity(locationCity);
      } else {
        setUserCity('');
      }
      return;
    }
    
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        async (position) => {
          try {
            const response = await fetch(
              `https://api.bigdatacloud.net/data/reverse-geocode-client?latitude=${position.coords.latitude}&longitude=${position.coords.longitude}&localityLanguage=en`
            );
            const data = await response.json();
            const detectedCity = data.city || data.locality || '';
            
            // Filter out server-side location names that are clearly wrong
            if (invalidLocations.includes(detectedCity.toLowerCase())) {
              // Use empty string - let user enter manually
              setUserCity('');
            } else {
              setUserCity(detectedCity);
            }
          } catch {
            setUserCity('');
          }
        },
        () => setUserCity(''),
        { timeout: 5000 }
      );
    }
  }, [locationCity]);
  
  const handleCTA = (mode) => {
    // Pass currency info to planning page
    navigate(`/planning?mode=${mode}${userCity ? `&from=${encodeURIComponent(userCity)}` : ''}${currency ? `&currency=${currency}` : ''}`);
  };
  
  // Handle feature card clicks - redirect to login if not authenticated
  const handleFeatureClick = (path) => {
    if (!isAuthenticated) {
      // Save intended destination and redirect to auth
      navigate(`/auth?redirect=${encodeURIComponent(path)}`);
    } else {
      navigate(path);
    }
  };
  
  const handleLogout = async () => {
    await logout();
    navigate('/');
  };
  
  return (
    <div className="min-h-screen bg-white overflow-x-hidden">
      {/* Landing Carousel - shows only on first visit */}
      <AnimatePresence>
        {showCarousel && (
          <LandingCarousel onClose={hideCarousel} />
        )}
      </AnimatePresence>
      
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-white/80 backdrop-blur-xl border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-4 md:px-8 py-3 flex items-center justify-between">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2" data-testid="logo-link">
            <PanchiBirdLogo className="w-10 h-10" />
            <span className="text-xl font-bold bg-gradient-to-r from-teal-600 to-cyan-600 bg-clip-text text-transparent">Panchi</span>
            <span className="hidden sm:inline px-3 py-1 bg-gradient-to-r from-amber-400 to-orange-500 text-white text-xs font-bold" style={{ borderRadius: '9999px' }}>
              BETA
            </span>
          </Link>
          
          {/* Center Nav */}
          <nav className="hidden md:flex items-center gap-1 p-1 bg-gray-100" style={{ borderRadius: '9999px' }}>
            {[
              { to: '/', icon: Home, label: 'Home' },
              { to: '/saved', icon: Bookmark, label: 'Saved' },
              { to: '/community', icon: Users, label: 'Community' },
            ].map(item => (
              <Link
                key={item.to}
                to={item.to}
                className={`flex items-center gap-2 px-5 py-2 transition-all ${
                  location.pathname === item.to 
                    ? 'bg-white text-teal-600 font-semibold shadow-sm' 
                    : 'text-gray-600 hover:text-teal-600'
                }`}
                style={{ borderRadius: '9999px' }}
              >
                <item.icon size={18} />
                <span>{item.label}</span>
              </Link>
            ))}
          </nav>
          
          {/* Right */}
          <div className="flex items-center gap-2">
            <LocationDropdown />
            {isAuthenticated ? (
              <div className="flex items-center gap-3">
              <Link 
                to="/profile"
                className="flex items-center gap-2 px-4 py-2 bg-gray-100 hover:bg-gray-200 transition-all"
                style={{ borderRadius: '9999px' }}
              >
                {user?.picture ? (
                  <img src={user.picture} alt="" className="w-8 h-8" style={{ borderRadius: '50%' }} />
                ) : (
                  <div className="w-8 h-8 bg-gradient-to-br from-teal-500 to-cyan-500 flex items-center justify-center text-white" style={{ borderRadius: '50%' }}>
                    <User size={16} />
                  </div>
                )}
                <span className="hidden sm:inline font-medium text-gray-700">{user?.name?.split(' ')[0]}</span>
              </Link>
              <button
                onClick={handleLogout}
                className="p-2.5 bg-gray-100 hover:bg-red-100 hover:text-red-600 text-gray-500 transition-all"
                style={{ borderRadius: '50%' }}
              >
                <LogOut size={18} />
              </button>
            </div>
          ) : (
              <Link
                to="/auth"
                className="flex items-center gap-2 px-6 py-2.5 bg-gradient-to-r from-teal-500 to-cyan-500 text-white font-semibold shadow-lg shadow-teal-500/25 hover:shadow-teal-500/40 transition-all"
                style={{ borderRadius: '9999px' }}
                data-testid="signin-btn"
              >
                <User size={18} />
                <span>Sign In</span>
              </Link>
            )}
          </div>
        </div>
      </header>
      
      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center pt-20 pb-32 md:pb-12 px-4 bg-gradient-to-br from-sky-50 via-white to-teal-50">
        {/* Animated Travel Objects */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <FloatingCloud className="top-24 left-10" delay={0} />
          <FloatingCloud className="top-40 right-20" delay={2} size="text-4xl" />
          <FloatingCloud className="bottom-40 left-1/4" delay={4} size="text-3xl" />
          <FloatingPlane className="top-32 right-1/4" delay={1} />
          <FloatingBalloon className="top-1/3 left-16" delay={0.5} />
          <FloatingSuitcase className="bottom-1/3 right-16" delay={1.5} />
          <FloatingGlobe className="bottom-20 left-1/3 hidden md:block" delay={0} />
          
          {/* Background blurs */}
          <div className="absolute top-20 -left-20 w-72 md:w-96 h-72 md:h-96 bg-teal-200/40 blur-3xl" style={{ borderRadius: '50%' }} />
          <div className="absolute bottom-20 -right-20 w-72 md:w-96 h-72 md:h-96 bg-cyan-200/40 blur-3xl" style={{ borderRadius: '50%' }} />
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[400px] md:w-[600px] h-[400px] md:h-[600px] bg-amber-100/30 blur-3xl" style={{ borderRadius: '50%' }} />
        </div>
        
        <div className="relative z-10 max-w-7xl mx-auto w-full">
          <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 items-center">
            {/* Left Content */}
            <div className="text-center lg:text-left">
              {/* Badge */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="inline-flex items-center gap-2 px-4 py-2 bg-white border border-teal-200 shadow-sm mb-6 md:mb-8"
                style={{ borderRadius: '9999px' }}
              >
                <Sparkles size={16} className="text-amber-500" />
                <span className="text-xs md:text-sm font-semibold text-gray-700">AI-Powered Travel Planning</span>
              </motion.div>
              
              {/* Main Heading - Unique Teal/Cyan Gradient */}
              <motion.h1
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 }}
                className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-black leading-[1.15] mb-4 md:mb-6 text-gray-900"
              >
                {isAuthenticated ? (
                  <>
                    Welcome back,<br />
                    <span className="bg-gradient-to-r from-teal-500 via-cyan-500 to-sky-500 bg-clip-text text-transparent">
                      {user?.name?.split(' ')[0]}!
                    </span>
                  </>
                ) : (
                  <>
                    Your AI Travel<br />
                    <span className="bg-gradient-to-r from-teal-500 via-cyan-500 to-sky-500 bg-clip-text text-transparent">
                      Companion
                    </span>
                  </>
                )}
              </motion.h1>
              
              {/* Description */}
              <motion.p
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
                className="text-base md:text-lg text-gray-600 max-w-xl mx-auto lg:mx-0 mb-8"
              >
                Let our AI create the perfect personalized itinerary for your next adventure. 
                Just tell us where you want to go.
              </motion.p>
              
              {/* CTA Buttons - Optimized for mobile */}
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 }}
                className="flex flex-col sm:flex-row flex-wrap justify-center lg:justify-start gap-2 sm:gap-3 mb-10"
              >
                {/* Primary - Teal Gradient Pill */}
                <button
                  onClick={() => handleCTA('plan')}
                  className="flex items-center justify-center gap-2 px-5 py-2.5 sm:px-6 sm:py-3.5 bg-gradient-to-r from-teal-500 to-cyan-500 text-white font-bold text-sm sm:text-base shadow-xl shadow-teal-500/30 hover:shadow-2xl hover:shadow-teal-500/40 hover:scale-105 transition-all duration-300"
                  style={{ borderRadius: '9999px' }}
                  data-testid="plan-trip-btn"
                >
                  <Map size={18} className="sm:w-5 sm:h-5" />
                  Start Planning
                  <ArrowRight size={16} className="sm:w-[18px] sm:h-[18px]" />
                </button>
                
                {/* Secondary - White Pill */}
                <button
                  onClick={() => handleCTA('discover')}
                  className="flex items-center justify-center gap-2 px-5 py-2.5 sm:px-6 sm:py-3.5 bg-white text-gray-700 font-bold text-sm sm:text-base border-2 border-gray-200 hover:border-teal-300 hover:text-teal-600 shadow-lg hover:shadow-xl transition-all duration-300"
                  style={{ borderRadius: '9999px' }}
                  data-testid="explore-btn"
                >
                  <Compass size={18} className="sm:w-5 sm:h-5" />
                  Explore Ideas
                </button>
                
                {/* Tertiary - Amber Pill */}
                <button
                  onClick={() => handleCTA('surprise')}
                  className="flex items-center justify-center gap-2 px-5 py-2.5 sm:px-6 sm:py-3.5 bg-gradient-to-r from-amber-400 to-orange-500 text-white font-bold text-sm sm:text-base shadow-lg shadow-amber-500/30 hover:shadow-xl hover:shadow-amber-500/40 hover:scale-105 transition-all duration-300"
                  style={{ borderRadius: '9999px' }}
                  data-testid="surprise-btn"
                >
                  <Sparkles size={18} className="sm:w-5 sm:h-5" />
                  Surprise Me
                </button>
              </motion.div>
              
              {/* Stats */}
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.5 }}
                className="flex flex-wrap justify-center lg:justify-start gap-6 md:gap-8"
              >
                {[
                  { value: '10K+', label: 'Plans Created', icon: '✨' },
                  { value: '500+', label: 'Destinations', icon: '🌍' },
                  { value: '4.9', label: 'User Rating', icon: '⭐' },
                ].map((stat, i) => (
                  <div key={i} className="text-center lg:text-left">
                    <div className="flex items-center gap-1.5 text-xl md:text-2xl font-black text-gray-900">
                      <span>{stat.icon}</span>
                      <span>{stat.value}</span>
                    </div>
                    <span className="text-xs md:text-sm text-gray-500">{stat.label}</span>
                  </div>
                ))}
              </motion.div>
            </div>
            
            {/* Right - Hero Image */}
            <motion.div
              initial={{ opacity: 0, x: 50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.3, duration: 0.6 }}
              className="relative hidden lg:block"
            >
              <div className="relative">
                {/* Main Image */}
                <div className="relative overflow-hidden shadow-2xl" style={{ borderRadius: '2rem' }}>
                  <AnimatePresence mode="wait">
                    <motion.img
                      key={currentBg}
                      src={HERO_IMAGES[currentBg]}
                      alt="Travel destination"
                      className="w-full h-[500px] object-cover"
                      initial={{ opacity: 0, scale: 1.1 }}
                      animate={{ opacity: 1, scale: 1 }}
                      exit={{ opacity: 0 }}
                      transition={{ duration: 1 }}
                    />
                  </AnimatePresence>
                  <div className="absolute inset-0 bg-gradient-to-t from-black/30 via-transparent to-transparent" />
                </div>
                
                {/* Floating Cards */}
                <motion.div 
                  className="absolute -top-4 -right-4 p-4 bg-white shadow-xl flex items-center gap-3"
                  style={{ borderRadius: '1rem' }}
                  animate={{ y: [0, -10, 0] }}
                  transition={{ duration: 3, repeat: Infinity }}
                >
                  <div className="w-12 h-12 bg-gradient-to-br from-teal-100 to-cyan-100 flex items-center justify-center text-2xl" style={{ borderRadius: '0.75rem' }}>
                    🏝️
                  </div>
                  <div>
                    <p className="font-bold text-gray-900">Maldives</p>
                    <p className="text-sm text-gray-500">5-day trip</p>
                  </div>
                </motion.div>
                
                <motion.div 
                  className="absolute -bottom-4 -left-4 p-4 bg-white shadow-xl flex items-center gap-3"
                  style={{ borderRadius: '1rem' }}
                  animate={{ y: [0, 10, 0] }}
                  transition={{ duration: 3.5, repeat: Infinity, delay: 0.5 }}
                >
                  <div className="w-12 h-12 bg-gradient-to-br from-amber-100 to-orange-100 flex items-center justify-center text-2xl" style={{ borderRadius: '0.75rem' }}>
                    🗼
                  </div>
                  <div>
                    <p className="font-bold text-gray-900">Paris</p>
                    <p className="text-sm text-gray-500">3-day trip</p>
                  </div>
                </motion.div>
                
                {/* Animated plane flying across */}
                <motion.div
                  className="absolute -top-8 left-0 text-4xl"
                  animate={{ 
                    x: [0, 400, 400],
                    y: [0, -20, -20],
                    opacity: [0, 1, 0]
                  }}
                  transition={{ 
                    duration: 8, 
                    repeat: Infinity,
                    ease: "easeInOut"
                  }}
                >
                  ✈️
                </motion.div>
                
                {/* Image indicators */}
                <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2">
                  {HERO_IMAGES.map((_, idx) => (
                    <button
                      key={idx}
                      onClick={() => setCurrentBg(idx)}
                      className={`h-2 transition-all ${idx === currentBg ? 'w-8 bg-white' : 'w-2 bg-white/50'}`}
                      style={{ borderRadius: '9999px' }}
                    />
                  ))}
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>
      
      {/* Feature Highlights Section */}
      <section className="py-16 md:py-24 px-4 bg-gradient-to-b from-white via-teal-50/30 to-white">
        <div className="max-w-7xl mx-auto">
          {/* Section Header */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12 md:mb-16"
          >
            <span className="inline-flex items-center gap-2 px-4 py-2 bg-teal-100 text-teal-700 text-sm font-bold mb-4" style={{ borderRadius: '9999px' }}>
              <Sparkles size={16} />
              WHY PANCHI?
            </span>
            <h2 className="text-3xl md:text-5xl font-black text-gray-900 mb-4">
              Everything You Need to <span className="text-teal-600">Travel Smart</span>
            </h2>
            <p className="text-gray-500 text-lg max-w-2xl mx-auto">
              AI-powered features designed to make your travel planning effortless and enjoyable
            </p>
          </motion.div>
          
          {/* Feature Cards Grid */}
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {/* Card 1: AI Day-by-Day Itinerary */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
              className="group relative p-6 bg-white border border-gray-100 shadow-lg hover:shadow-2xl hover:border-teal-200 transition-all overflow-hidden"
              style={{ borderRadius: '1.5rem' }}
            >
              <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-teal-100 to-cyan-100 opacity-50" style={{ borderRadius: '0 1.5rem 0 100%' }} />
              <div className="relative">
                <div className="w-14 h-14 bg-gradient-to-br from-teal-500 to-cyan-500 flex items-center justify-center mb-5 shadow-lg shadow-teal-500/30" style={{ borderRadius: '1rem' }}>
                  <Calendar size={28} className="text-white" />
                </div>
                <h3 className="text-xl font-black text-gray-900 mb-2">AI Day-by-Day Itinerary</h3>
                <p className="text-gray-500 mb-6 leading-relaxed">
                  Get a personalized, hour-by-hour travel plan crafted by AI based on your preferences, budget, and travel style.
                </p>
                <button
                  onClick={() => handleFeatureClick('/planning')}
                  className="flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r from-teal-500 to-cyan-500 text-white font-bold shadow-md hover:shadow-lg hover:scale-105 transition-all"
                  style={{ borderRadius: '9999px' }}
                  data-testid="feature-itinerary-btn"
                >
                  Plan My Trip
                  <ArrowRight size={16} />
                </button>
              </div>
            </motion.div>
            
            {/* Card 2: Best Travel Options */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
              className="group relative p-6 bg-white border border-gray-100 shadow-lg hover:shadow-2xl hover:border-amber-200 transition-all overflow-hidden"
              style={{ borderRadius: '1.5rem' }}
            >
              <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-amber-100 to-orange-100 opacity-50" style={{ borderRadius: '0 1.5rem 0 100%' }} />
              <div className="relative">
                <div className="w-14 h-14 bg-gradient-to-br from-amber-500 to-orange-500 flex items-center justify-center mb-5 shadow-lg shadow-amber-500/30" style={{ borderRadius: '1rem' }}>
                  <Plane size={28} className="text-white" />
                </div>
                <h3 className="text-xl font-black text-gray-900 mb-2">Best Travel Options</h3>
                <p className="text-gray-500 mb-6 leading-relaxed">
                  AI-ranked flights, trains, and buses. Compare prices, duration, and comfort to find your perfect travel option.
                </p>
                <button
                  onClick={() => handleFeatureClick('/planning')}
                  className="flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r from-amber-500 to-orange-500 text-white font-bold shadow-md hover:shadow-lg hover:scale-105 transition-all"
                  style={{ borderRadius: '9999px' }}
                  data-testid="feature-transport-btn"
                >
                  Compare Options
                  <ArrowRight size={16} />
                </button>
              </div>
            </motion.div>
            
            {/* Card 3: Events & Experiences */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.3 }}
              className="group relative p-6 bg-white border border-gray-100 shadow-lg hover:shadow-2xl hover:border-rose-200 transition-all overflow-hidden"
              style={{ borderRadius: '1.5rem' }}
            >
              <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-rose-100 to-pink-100 opacity-50" style={{ borderRadius: '0 1.5rem 0 100%' }} />
              <div className="relative">
                <div className="w-14 h-14 bg-gradient-to-br from-rose-500 to-pink-500 flex items-center justify-center mb-5 shadow-lg shadow-rose-500/30" style={{ borderRadius: '1rem' }}>
                  <Star size={28} className="text-white" />
                </div>
                <h3 className="text-xl font-black text-gray-900 mb-2">Events & Experiences</h3>
                <p className="text-gray-500 mb-6 leading-relaxed">
                  Discover concerts, festivals, sports events, and local experiences happening during your travel dates.
                </p>
                <button
                  onClick={() => handleFeatureClick('/community?tab=events')}
                  className="flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r from-rose-500 to-pink-500 text-white font-bold shadow-md hover:shadow-lg hover:scale-105 transition-all"
                  style={{ borderRadius: '9999px' }}
                  data-testid="feature-events-btn"
                >
                  Explore Events
                  <ArrowRight size={16} />
                </button>
              </div>
            </motion.div>
            
            {/* Card 4: Group Travel & Travel Buddies */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.4 }}
              className="group relative p-6 bg-white border border-gray-100 shadow-lg hover:shadow-2xl hover:border-violet-200 transition-all overflow-hidden"
              style={{ borderRadius: '1.5rem' }}
            >
              <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-violet-100 to-purple-100 opacity-50" style={{ borderRadius: '0 1.5rem 0 100%' }} />
              <div className="relative">
                <div className="w-14 h-14 bg-gradient-to-br from-violet-500 to-purple-500 flex items-center justify-center mb-5 shadow-lg shadow-violet-500/30" style={{ borderRadius: '1rem' }}>
                  <Users size={28} className="text-white" />
                </div>
                <h3 className="text-xl font-black text-gray-900 mb-2">Group Travel & Buddies</h3>
                <p className="text-gray-500 mb-6 leading-relaxed">
                  Plan group trips with friends or find like-minded travel companions heading to the same destination.
                </p>
                <button
                  onClick={() => handleFeatureClick('/find-travel-buddy')}
                  className="flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r from-violet-500 to-purple-500 text-white font-bold shadow-md hover:shadow-lg hover:scale-105 transition-all"
                  style={{ borderRadius: '9999px' }}
                  data-testid="feature-community-btn"
                >
                  Find Buddies
                  <ArrowRight size={16} />
                </button>
              </div>
            </motion.div>
            
            {/* Card 5: Community Trends */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.5 }}
              className="group relative p-6 bg-white border border-gray-100 shadow-lg hover:shadow-2xl hover:border-emerald-200 transition-all overflow-hidden md:col-span-2 lg:col-span-1"
              style={{ borderRadius: '1.5rem' }}
            >
              <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-emerald-100 to-green-100 opacity-50" style={{ borderRadius: '0 1.5rem 0 100%' }} />
              <div className="relative">
                <div className="w-14 h-14 bg-gradient-to-br from-emerald-500 to-green-500 flex items-center justify-center mb-5 shadow-lg shadow-emerald-500/30" style={{ borderRadius: '1rem' }}>
                  <TrendingUp size={28} className="text-white" />
                </div>
                <h3 className="text-xl font-black text-gray-900 mb-2">Community Trends</h3>
                <p className="text-gray-500 mb-6 leading-relaxed">
                  See what's trending! Discover where other travelers are heading and get inspired by popular destinations.
                </p>
                <button
                  onClick={() => handleFeatureClick('/community?tab=broadcasts')}
                  className="flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r from-emerald-500 to-green-500 text-white font-bold shadow-md hover:shadow-lg hover:scale-105 transition-all"
                  style={{ borderRadius: '9999px' }}
                  data-testid="feature-trends-btn"
                >
                  View Trends
                  <ArrowRight size={16} />
                </button>
              </div>
            </motion.div>
          </div>
        </div>
      </section>
      
      {/* Destinations Section */}
      <section className="py-12 md:py-20 px-4 bg-white">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-8 md:mb-12">
            <div>
              <div className="flex items-center gap-2 mb-2">
                <Globe size={18} className="text-teal-600" />
                <span className="text-xs md:text-sm font-bold text-teal-600 uppercase tracking-wider">Worldwide</span>
              </div>
              <h2 className="text-2xl md:text-4xl font-black text-gray-900">
                Trending <span className="text-teal-600">Destinations</span>
              </h2>
            </div>
            <Link
              to="/community"
              className="flex items-center gap-2 px-5 py-2.5 bg-gray-100 text-gray-700 font-semibold text-sm hover:bg-teal-100 hover:text-teal-600 transition-all"
              style={{ borderRadius: '9999px' }}
            >
              View All
              <ArrowRight size={16} />
            </Link>
          </div>
          
          {/* Destinations Grid */}
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 md:gap-4">
            {DESTINATIONS.map((dest, index) => (
              <motion.button
                key={dest.name}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                whileHover={{ y: -8 }}
                onClick={() => navigate(`/planning?mode=plan&destination=${encodeURIComponent(dest.name)}&from=${encodeURIComponent(userCity)}`)}
                className="group relative aspect-[3/4] overflow-hidden cursor-pointer shadow-lg hover:shadow-xl transition-shadow"
                style={{ borderRadius: '1.25rem' }}
                data-testid={`destination-${dest.name.toLowerCase()}`}
              >
                <img 
                  src={dest.image} 
                  alt={dest.name}
                  className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />
                
                {dest.trending && (
                  <span className="absolute top-2 left-2 px-2 py-0.5 bg-gradient-to-r from-amber-400 to-orange-500 text-white text-[10px] md:text-xs font-bold shadow-lg" style={{ borderRadius: '9999px' }}>
                    🔥 Hot
                  </span>
                )}
                
                <div className="absolute top-2 right-2 flex items-center gap-1 px-1.5 py-0.5 bg-white/90 backdrop-blur-sm text-[10px] md:text-xs font-medium text-gray-700" style={{ borderRadius: '9999px' }}>
                  <Star size={10} className="text-amber-500 fill-amber-500" />
                  {dest.rating}
                </div>
                
                <div className="absolute bottom-0 left-0 right-0 p-3 md:p-4">
                  <div className="flex items-center gap-1.5 mb-0.5">
                    <span className="text-base md:text-lg">{dest.flag}</span>
                  </div>
                  <h3 className="text-sm md:text-lg font-bold text-white">{dest.name}</h3>
                  <p className="text-white/70 text-[10px] md:text-xs mb-1">{dest.country}</p>
                  
                  {/* Transport Options */}
                  {dest.transport && (
                    <div className="flex items-center gap-2">
                      {dest.transport.flight && (
                        <span className="flex items-center gap-0.5 px-1.5 py-0.5 bg-white/20 backdrop-blur-sm text-[9px] text-white" style={{ borderRadius: '4px' }}>
                          <Plane size={10} />
                        </span>
                      )}
                      {dest.transport.train && (
                        <span className="flex items-center gap-0.5 px-1.5 py-0.5 bg-emerald-500/80 backdrop-blur-sm text-[9px] text-white" style={{ borderRadius: '4px' }}>
                          <Train size={10} />
                          {dest.transport.trainTime && <span className="hidden md:inline ml-0.5">{dest.transport.trainTime.split(' ')[0]}</span>}
                        </span>
                      )}
                    </div>
                  )}
                </div>
              </motion.button>
            ))}
          </div>
        </div>
      </section>
      
      {/* Upcoming Events Section - MOVED BEFORE WHY PANCHI */}
      <section className="py-12 md:py-20 px-4 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col md:flex-row items-start md:items-center justify-between mb-10">
            <div>
              <div className="flex items-center gap-2 mb-3">
                <Calendar size={18} className="text-rose-500" />
                <span className="text-xs md:text-sm font-bold text-rose-600 uppercase tracking-wider">
                  Plan Your Holidays
                </span>
              </div>
              <h2 className="text-2xl md:text-4xl font-black text-gray-900">
                Upcoming Events <span className="text-rose-500">Worth Traveling For</span>
              </h2>
            </div>
            <Link
              to="/community?tab=events"
              className="mt-4 md:mt-0 flex items-center gap-2 px-5 py-2.5 bg-gray-100 text-gray-700 font-semibold text-sm hover:bg-rose-100 hover:text-rose-600 transition-all"
              style={{ borderRadius: '9999px' }}
            >
              View All Events
              <ArrowRight size={16} />
            </Link>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
            {nearbyEvents.map((event, index) => (
              <motion.div
                key={event.name}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="group relative overflow-hidden shadow-lg hover:shadow-xl transition-shadow bg-white border border-gray-100"
                style={{ borderRadius: '1.25rem' }}
                data-testid={`event-card-${index}`}
              >
                <div className="aspect-video relative overflow-hidden">
                  <img 
                    src={event.image || 'https://images.unsplash.com/photo-1492684223066-81342ee5ff30?w=400&q=80'} 
                    alt={event.name}
                    className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                    onError={(e) => {
                      const fallbacks = {
                        sports: 'https://images.unsplash.com/photo-1461896836934-ffe607ba8211?w=600&q=80',
                        festival: 'https://images.unsplash.com/photo-1533174072545-7a4b6ad7a6c3?w=600&q=80',
                        music: 'https://images.unsplash.com/photo-1470229722913-7c0e2dbbafd3?w=600&q=80',
                        concert: 'https://images.unsplash.com/photo-1493225457124-a3eb161ffa5f?w=600&q=80',
                        cultural: 'https://images.unsplash.com/photo-1523580494863-6f3031224c94?w=600&q=80',
                        food: 'https://images.unsplash.com/photo-1555939594-58d7cb561ad1?w=600&q=80',
                      };
                      e.target.src = fallbacks[event.category] || 'https://images.unsplash.com/photo-1492684223066-81342ee5ff30?w=400&q=80';
                      e.target.onerror = null;
                    }}
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
                  <span className="absolute top-2 right-2 px-2 py-0.5 bg-white/90 backdrop-blur-sm text-[10px] md:text-xs font-bold text-gray-700" style={{ borderRadius: '9999px' }}>
                    {event.category}
                  </span>
                </div>
                
                <div className="p-4">
                  <h3 className="text-base font-bold text-gray-900 mb-1 line-clamp-1">{event.name}</h3>
                  <div className="flex items-center gap-2 text-sm text-gray-500 mb-2">
                    <MapPin size={12} />
                    <span>{event.city}, {event.country}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2 text-sm font-semibold text-rose-600">
                      <Calendar size={12} />
                      <span>{event.date}</span>
                    </div>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        navigate(`/planning?destination=${encodeURIComponent(event.city)}&event=${encodeURIComponent(event.name)}&from=${encodeURIComponent(locationCity || '')}`);
                      }}
                      className="flex items-center gap-1 px-3 py-1.5 bg-gradient-to-r from-teal-500 to-cyan-500 text-white text-xs font-semibold rounded-full shadow-md hover:shadow-lg hover:scale-105 transition-all"
                      data-testid={`plan-trip-btn-${index}`}
                    >
                      <Plane size={12} />
                      Plan Trip
                    </button>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
      
      {/* CTA Section - Light Theme with Travel Animations */}
      <section className="py-16 md:py-24 px-4 bg-white relative overflow-hidden">
        {/* Animated background elements */}
        <div className="absolute inset-0 pointer-events-none">
          <FloatingCloud className="top-10 left-10" delay={0} size="text-6xl" />
          <FloatingCloud className="top-20 right-20" delay={1} size="text-5xl" />
          <FloatingCloud className="bottom-10 left-1/4" delay={2} size="text-4xl" />
          <FloatingPlane className="top-1/4 right-1/3" delay={0.5} />
          <FloatingBalloon className="bottom-1/4 left-10" delay={1} />
          <FloatingSuitcase className="top-1/3 right-10" delay={1.5} />
        </div>
        
        <div className="max-w-4xl mx-auto relative z-10">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            className="relative p-10 md:p-16 overflow-hidden text-center"
            style={{ 
              borderRadius: '2.5rem',
              background: 'linear-gradient(135deg, #f0fdfa 0%, #ccfbf1 25%, #99f6e4 50%, #5eead4 75%, #2dd4bf 100%)'
            }}
          >
            {/* Inner decorations */}
            <div className="absolute top-0 right-0 w-40 h-40 bg-white/30 blur-2xl" style={{ borderRadius: '50%' }} />
            <div className="absolute bottom-0 left-0 w-32 h-32 bg-white/30 blur-2xl" style={{ borderRadius: '50%' }} />
            
            <div className="relative z-10">
              <motion.div
                animate={{ y: [0, -10, 0], rotate: [0, 5, 0] }}
                transition={{ duration: 4, repeat: Infinity }}
                className="mb-6"
              >
                <PanchiBirdLogo className="w-16 h-16 md:w-20 md:h-20 mx-auto" />
              </motion.div>
              
              <h2 className="text-2xl md:text-4xl font-black text-teal-900 mb-3 md:mb-4">
                Ready to Take Flight?
              </h2>
              <p className="text-teal-800/70 text-base md:text-lg mb-8 md:mb-10 max-w-xl mx-auto">
                {"Join our growing community of travelers discovering AI-powered trip planning."}
              </p>
              
              <button
                onClick={() => handleCTA('plan')}
                className="inline-flex items-center gap-2 md:gap-3 px-8 md:px-10 py-4 md:py-5 bg-white text-teal-700 font-bold text-base md:text-lg shadow-xl hover:shadow-2xl hover:scale-105 transition-all duration-300"
                style={{ borderRadius: '9999px' }}
              >
                <Plane size={22} />
                Start Your Journey
                <ArrowRight size={20} />
              </button>
            </div>
            
            {/* Floating plane animation across the section */}
            <motion.div
              className="absolute top-8 -left-10 text-3xl"
              animate={{ 
                x: [0, 500],
                y: [0, -30, 0, -20, 0],
              }}
              transition={{ 
                duration: 10, 
                repeat: Infinity,
                ease: "linear"
              }}
            >
              ✈️
            </motion.div>
          </motion.div>
        </div>
      </section>
      
      {/* Footer */}
      <footer className="py-12 px-4 bg-gray-50 border-t border-gray-200">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6 mb-8">
            <div className="flex items-center gap-3">
              <PanchiBirdLogo className="w-10 h-10" />
              <div>
                <span className="text-xl font-bold bg-gradient-to-r from-teal-600 to-cyan-600 bg-clip-text text-transparent">Panchi</span>
                <span className="block text-xs text-gray-500">Fly Free, Explore More</span>
              </div>
            </div>
            
            {/* Upgrade to Pro Section */}
            <div className="flex flex-col items-center gap-3">
              <p className="text-sm text-gray-600 font-medium">Unlock the full travel experience</p>
              <Link 
                to="/profile"
                className="flex items-center gap-2 px-6 py-2.5 bg-gradient-to-r from-teal-500 to-cyan-500 text-white font-semibold shadow-lg shadow-teal-500/30 hover:shadow-teal-500/50 hover:scale-105 transition-all"
                style={{ borderRadius: '9999px' }}
                data-testid="upgrade-pro-btn"
              >
                <Sparkles size={18} />
                Go Pro
              </Link>
              <p className="text-xs text-gray-400">Unlimited plans • Priority AI • Exclusive features</p>
            </div>
          </div>
          
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="flex flex-wrap items-center justify-center gap-6 text-sm">
              {[
                { label: 'About', to: '/about' },
                { label: 'Support', to: '/support' },
                { label: 'Community', to: '/community' },
                { label: 'Find Travel Buddy', to: '/find-travel-buddy' },
                { label: 'Blog', to: '/blog' },
                { label: 'Privacy Policy', to: '/privacy' },
                { label: 'Terms of Service', to: '/terms' },
                { label: 'Admin', to: '/admin-login' },
              ].map(link => (
                <Link 
                  key={link.label} 
                  to={link.to} 
                  className="text-gray-500 hover:text-teal-600 transition-colors font-medium"
                >
                  {link.label}
                </Link>
              ))}
            </div>
            
            <p className="text-sm text-gray-400">
              © 2026 Panchi. Made with 💚 for travelers.
            </p>
          </div>
        </div>
      </footer>
      
      {/* Mobile Bottom Nav */}
      <div className="md:hidden fixed bottom-0 left-0 right-0 z-50 px-4 pb-4 bg-white border-t border-gray-100">
        <div className="flex items-center justify-around py-2">
          <Link to="/" className={`flex flex-col items-center gap-1 p-2 ${location.pathname === '/' ? 'text-teal-600' : 'text-gray-400'}`}>
            <Home size={22} />
            <span className="text-xs font-medium">Home</span>
          </Link>
          <Link to="/saved" className={`flex flex-col items-center gap-1 p-2 ${location.pathname === '/saved' ? 'text-teal-600' : 'text-gray-400'}`}>
            <Bookmark size={22} />
            <span className="text-xs font-medium">Saved</span>
          </Link>
          <Link 
            to="/planning"
            className="w-14 h-14 -mt-6 bg-gradient-to-r from-teal-500 to-cyan-500 text-white shadow-lg shadow-teal-500/30 flex items-center justify-center"
            style={{ borderRadius: '50%' }}
          >
            <MapPin size={24} />
          </Link>
          <Link to="/community" className={`flex flex-col items-center gap-1 p-2 ${location.pathname === '/community' ? 'text-teal-600' : 'text-gray-400'}`}>
            <Users size={22} />
            <span className="text-xs font-medium">Community</span>
          </Link>
          <Link to="/profile" className={`flex flex-col items-center gap-1 p-2 ${location.pathname === '/profile' ? 'text-teal-600' : 'text-gray-400'}`}>
            <User size={22} />
            <span className="text-xs font-medium">Profile</span>
          </Link>
        </div>
      </div>
      
      {/* Floating Help Button */}
      <FloatingHelpButton />
    </div>
  );
}
